<script setup lang="ts">
import ListingEditorPage from "./ListingEditorPage.vue";
import ListingEditorForm from "./ListingEditorForm.vue";
import { computed, ref, watch, useTemplateRef } from "vue";
/** @ts-ignore */
import { Container, Draggable } from "vue3-smooth-dnd";
import PackagedServiceForm from "./PackagedServiceForm.vue";
import PackagedServiceCard from "./PackagedServiceCard.vue";
import PlanLimitNotice from "./PlanLimitNotice.vue";
import { useListingPackagedServicesStore } from "./composables/useListingPackagedServicesStore";
import { usePackagedServices } from "./composables/usePackagedServices";
import { useProfileDetails } from "./composables/useProfileDetails";
import { useDragRotation } from "@/app/composables/useDragRotation";
import type { UiFileUploadItem } from "@shopexperts/shared/dist/types/ui/UiFileUpload.vue";
import type { PriceType } from "@shopexperts/shared";
import { PRICE_TYPE } from "@shopexperts/shared";

const props = defineProps<{
    locked?: boolean;
}>();

const asideOpen = ref(false);
const formEl = useTemplateRef('formEl');
const editIndex = ref<number | null>(null);

/** TODO: Why is this using inline type? */
const formModel = ref<{ 
    images?: UiFileUploadItem[]; 
    galleryImages?: UiFileUploadItem[];
    serviceName: string; 
    price: number; 
    priceType: PriceType;
    description?: string;
}>({ serviceName: '', price: 0, priceType: PRICE_TYPE.FIXED, description: '' });

const packagedStore = useListingPackagedServicesStore();
const { model: packagedServicesModel, initial: packagedServicesInitial } = packagedStore;

const { packagedServicesLimit } = useProfileDetails();
const isAtLimit = computed(() =>
    packagedServicesLimit.value !== null && packagedServicesModel.value.length >= packagedServicesLimit.value);

const { data: packagedFromApi } = usePackagedServices();
watch(packagedFromApi, (val) => {
    if (!val || !Array.isArray(val)) return;
    const mapped = val.map((p) => ({
        id: Number(p.id) || null,
        service_name: p.service_name || '',
        starting_price: Number(p.starting_price) || 0,
        price_type: p.price_type || PRICE_TYPE.FIXED,
        thumbnail: { url: p.featured_media?.url, media_id: p.featured_media?.id, isTemporary: false },
        featured_media_id: p.featured_media?.id,
        gallery_images: p.gallery_media?.map((media) => ({ 
            url: media.url, 
            media_id: media.id, 
            isTemporary: false 
        })) || [],
        description: p.description || '',
    }));
    packagedServicesInitial.value = mapped;
    packagedServicesModel.value = mapped;
}, { immediate: true });

function onActionClick() {
    if (editIndex.value != null) {
        formEl.value?.resetForm();
    }
    editIndex.value = null;
    asideOpen.value = true;
}

function onSubmit(payload: any) {
    if (editIndex.value == null && isAtLimit.value) {
        asideOpen.value = false;
        return;
    }
    if (editIndex.value != null) {
        const current = packagedServicesModel.value[editIndex.value];
        packagedStore.updateAt(editIndex.value, { ...payload, id: current?.id ?? null });
    } else {
        packagedStore.add(payload);
    }
    asideOpen.value = false;
    formEl.value?.resetForm();
}

function onEdit(index: number) {
    editIndex.value = index;
    const item = packagedServicesModel.value[index] as any;
    const url = typeof item?.thumbnail === 'string' ? (item.thumbnail || '') : (item?.thumbnail?.url || '');
    const file = typeof item?.thumbnail === 'object' ? item?.thumbnail?.file : undefined;
    formModel.value = {
        serviceName: item?.service_name || '',
        price: Number(item?.starting_price) || 0,
        priceType: item?.price_type || PRICE_TYPE.FIXED,
        images: (url || file) ? [{ url, file, isTemporary: false, media_id: item?.featured_media_id }] : undefined,
        galleryImages: item?.gallery_images || [],
        description: item?.description || '',
    };
    asideOpen.value = true;
}

function onDelete(index: number) {
    packagedStore.removeAt(index);
}

// Use drag rotation composable for dynamic rotation effects
const { onDragStart, onDragEnd } = useDragRotation({
    containerSelector: '.flex.flex-col.gap-6',
    baseRotation: 0,
    velocityMultiplierX: 3,
    velocityMultiplierY: 3.5,
    minAdjustment: -5.5,
    maxAdjustment: 5.5,
});

function onDrop(dropResult: { removedIndex: number | null; addedIndex: number | null }) {
    if (dropResult.removedIndex !== null && dropResult.addedIndex !== null) {
        packagedStore.reorder(dropResult.removedIndex, dropResult.addedIndex);
    }
    onDragEnd();
}
</script>

<template>
    <ListingEditorPage heading="Packaged Services" action="Add Service" :locked="props.locked"
        :action-disabled="isAtLimit"
        @action-click="onActionClick"
        empty-state-text="Create your first packaged service like “Shopify Store Setup – $499” and show leads exactly what they’ll get."
        empty-state-icon="package-placeholder">
        <template #notice>
            <PlanLimitNotice feature="packaged_services" :limit="packagedServicesLimit" :count="packagedServicesModel.length" />
        </template>
        <template v-if="packagedServicesModel.length" #content>
            <Container 
                @drop="onDrop"
                @drag-start="onDragStart"
                @drag-end="onDragEnd"
                drag-handle-selector=".drag-handle" 
                drag-class="dragging"
                :animation-duration="300"
                class="flex flex-col gap-6"
            >
                <Draggable v-for="(item, index) in packagedServicesModel" :key="item.id || `packaged-${item.service_name}-${index}`">
                    <PackagedServiceCard 
                        :item="item"
                        :index="index" 
                        @edit="onEdit(index)" 
                        @delete="onDelete(index)" 
                    />
                </Draggable>
            </Container>
        </template>
    </ListingEditorPage>


    <ListingEditorForm :title="editIndex != null ? 'Edit Packaged Service' : 'Add Packaged Service'"
        description="Highlight your skills with a clear service package clients can purchase directly."
        :submit-label="editIndex != null ? 'Save Packaged Service' : 'Add Packaged Service'" v-model:open="asideOpen">
        <template #form>
            <PackagedServiceForm ref="formEl" v-model="formModel" :submit-label="editIndex != null ? 'Save Packaged Service' : 'Add Packaged Service'"
                @submit="onSubmit" />
        </template>
    </ListingEditorForm>
</template>